/**
 * FrictionListView Component
 * Displays frictions grouped by regulatory framework with filtering, sorting, and search
 * Requirements: 18.1-18.20
 */

import React, { useState, useEffect, useMemo } from 'react';
import { FrameworkGroup, FrictionItem } from './FrameworkGroup';
import { ExpandableListItem } from './ExpandableList';

interface FrictionListViewProps {
  frictions: FrictionItem[];
  affectedItemsCache: Map<string, { entities?: ExpandableListItem[], relationships?: ExpandableListItem[], count: number }>;
  loadingAffectedItems: Set<string>;
  onEditFriction: (friction: FrictionItem) => void;
  onCopyFriction: (friction: FrictionItem) => void;
  onRemoveFriction: (frictionId: string) => void;
  onNavigateToEntity: (entityId: string) => void;
  onNavigateToRelationship: (relationshipId: string) => void;
  onFetchAffectedEntities: (frictionId: string) => void;
  onFetchAffectedRelationships: (frictionId: string) => void;
  onAddFriction?: (framework?: string) => void;
  initialFrictionTypeFilter?: 'all' | 'entity' | 'relationship'; // New prop for initial filter
}

export const FrictionListView: React.FC<FrictionListViewProps> = ({
  frictions,
  affectedItemsCache,
  loadingAffectedItems,
  onEditFriction,
  onCopyFriction,
  onRemoveFriction,
  onNavigateToEntity,
  onNavigateToRelationship,
  onFetchAffectedEntities,
  onFetchAffectedRelationships,
  onAddFriction,
  initialFrictionTypeFilter = 'all' // Default to showing all
}) => {
  // Filter and sort state
  const [frictionTypeFilter, setFrictionTypeFilter] = useState<'all' | 'entity' | 'relationship'>(initialFrictionTypeFilter);
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'magnitude' | 'affected'>('name');
  const [searchQuery, setSearchQuery] = useState('');

  // Framework group expanded state (persisted to localStorage)
  const [expandedFrameworks, setExpandedFrameworks] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('frictionManagement_expandedFrameworks');
      return saved ? new Set(JSON.parse(saved)) : new Set(['GDPR', 'NIS2', 'DORA', 'HIPAA', 'PCI_DSS', 'Canadian_Privacy', 'Other']);
    } catch {
      return new Set(['GDPR', 'NIS2', 'DORA', 'HIPAA', 'PCI_DSS', 'Canadian_Privacy', 'Other']);
    }
  });

  // Persist expanded state to localStorage
  useEffect(() => {
    localStorage.setItem('frictionManagement_expandedFrameworks', JSON.stringify(Array.from(expandedFrameworks)));
  }, [expandedFrameworks]);

  // Extract framework from friction (top-level field only)
  const getFramework = (friction: FrictionItem): string => {
    return friction.regulatoryFramework || 'Other';
  };

  // Filter frictions
  const filteredFrictions = useMemo(() => {
    let result = frictions;

    // Apply friction type filter
    if (frictionTypeFilter === 'entity') {
      result = result.filter(f => f.frictionType === 'entity_friction' || f.frictionType === 'regulatory_friction');
    } else if (frictionTypeFilter === 'relationship') {
      result = result.filter(f => f.frictionType === 'relationship_friction');
    }

    // Apply framework filter
    if (frameworkFilter !== 'all') {
      result = result.filter(f => getFramework(f) === frameworkFilter);
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(f => 
        f.name.toLowerCase().includes(query) ||
        (f.logicFormula && f.logicFormula.toLowerCase().includes(query)) ||
        (f.conditions && f.conditions.toLowerCase().includes(query))
      );
    }

    return result;
  }, [frictions, frictionTypeFilter, frameworkFilter, searchQuery]);

  // Sort frictions
  const sortedFrictions = useMemo(() => {
    const sorted = [...filteredFrictions];

    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'magnitude':
        sorted.sort((a, b) => {
          const magA = a.magnitude || a.baselineTension || 0;
          const magB = b.magnitude || b.baselineTension || 0;
          return magB - magA; // Descending
        });
        break;
      case 'affected':
        sorted.sort((a, b) => {
          const countA = affectedItemsCache.get(a.id)?.count || 0;
          const countB = affectedItemsCache.get(b.id)?.count || 0;
          return countB - countA; // Descending
        });
        break;
    }

    return sorted;
  }, [filteredFrictions, sortBy, affectedItemsCache]);

  // Group frictions by framework, separating non-framework from framework
  const { nonFrameworkFrictions, frameworkFrictions } = useMemo(() => {
    const nonFramework: FrictionItem[] = [];
    const framework: Record<string, FrictionItem[]> = {};
    
    sortedFrictions.forEach(friction => {
      const fw = getFramework(friction);
      if (fw === 'Other') {
        nonFramework.push(friction);
      } else {
        if (!framework[fw]) {
          framework[fw] = [];
        }
        framework[fw].push(friction);
      }
    });

    return { nonFrameworkFrictions: nonFramework, frameworkFrictions: framework };
  }, [sortedFrictions]);

  // Get frameworks in order (dynamically from the data)
  const frameworks = useMemo(() => {
    return Object.keys(frameworkFrictions).sort();
  }, [frameworkFrictions]);

  // Auto-expand frameworks with search matches
  useEffect(() => {
    if (searchQuery.trim()) {
      const matchingFrameworks = new Set<string>();
      Object.entries(frameworkFrictions).forEach(([framework, frictions]) => {
        if (frictions.length > 0) {
          matchingFrameworks.add(framework);
        }
      });
      setExpandedFrameworks(matchingFrameworks);
    }
  }, [searchQuery, frameworkFrictions]);

  const toggleFramework = (framework: string) => {
    setExpandedFrameworks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(framework)) {
        newSet.delete(framework);
      } else {
        newSet.add(framework);
      }
      return newSet;
    });
  };

  const matchCount = sortedFrictions.length;
  const totalCount = frictions.length;

  return (
    <div className="friction-list-view">
      {/* Controls Bar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '12px',
        marginBottom: '16px',
        padding: '16px',
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: '8px'
      }}>
        {/* Search */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
            Search Frictions
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or formula..."
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          {searchQuery && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              {matchCount} of {totalCount} friction{totalCount !== 1 ? 's' : ''} match
            </div>
          )}
        </div>

        {/* Friction Type Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
            Friction Type
          </label>
          <select
            value={frictionTypeFilter}
            onChange={(e) => setFrictionTypeFilter(e.target.value as 'all' | 'entity' | 'relationship')}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white'
            }}
          >
            <option value="all">All Types</option>
            <option value="entity">Entity Frictions</option>
            <option value="relationship">Relationship Frictions</option>
          </select>
        </div>

        {/* Framework Filter */}
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
            Framework
          </label>
          <select
            value={frameworkFilter}
            onChange={(e) => setFrameworkFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white'
            }}
          >
            <option value="all">All Frameworks</option>
            <option value="GDPR">GDPR</option>
            <option value="NIS2">NIS 2</option>
            <option value="DORA">DORA</option>
            <option value="HIPAA">HIPAA</option>
            <option value="PCI_DSS">PCI DSS</option>
            <option value="Canadian_Privacy">Canadian Privacy</option>
            <option value="Other">Other / Untagged</option>
          </select>
        </div>

        {/* Sort By */}
        <div>
          <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#374151' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'magnitude' | 'affected')}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              background: 'white'
            }}
          >
            <option value="name">Name (A-Z)</option>
            <option value="magnitude">Magnitude (High-Low)</option>
            <option value="affected">Affected Count (High-Low)</option>
          </select>
        </div>
      </div>

      {/* Framework Groups */}
      {frameworks.length === 0 && nonFrameworkFrictions.length === 0 ? (
        <div style={{
          padding: '48px 24px',
          textAlign: 'center',
          color: '#6b7280',
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px'
        }}>
          {searchQuery ? (
            <>
              <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                No frictions match your search
              </div>
              <div style={{ fontSize: '14px' }}>
                Try adjusting your search query or filters
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                No frictions defined
              </div>
              <div style={{ fontSize: '14px' }}>
                Create entity or relationship frictions using the tabs above
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Non-Framework Frictions (at top, expanded) - only show if there are any */}
          {nonFrameworkFrictions.length > 0 && (
            <FrameworkGroup
              key="Other"
              frameworkName="Other"
              frameworkDisplayName="Custom Frictions (No Framework)"
              frictions={nonFrameworkFrictions}
              affectedItemsCache={affectedItemsCache}
              loadingAffectedItems={loadingAffectedItems}
              onEditFriction={onEditFriction}
              onCopyFriction={onCopyFriction}
              onRemoveFriction={onRemoveFriction}
              onNavigateToEntity={onNavigateToEntity}
              onNavigateToRelationship={onNavigateToRelationship}
              onFetchAffectedEntities={onFetchAffectedEntities}
              onFetchAffectedRelationships={onFetchAffectedRelationships}
              onAddFriction={onAddFriction ? () => onAddFriction('Other') : undefined}
            />
          )}

          {/* Framework Frictions (collapsed by default) */}
          {frameworks.map(framework => (
            <FrameworkGroup
              key={framework}
              frameworkName={framework}
              frameworkDisplayName={framework}
              frictions={frameworkFrictions[framework] || []}
              affectedItemsCache={affectedItemsCache}
              loadingAffectedItems={loadingAffectedItems}
              onEditFriction={onEditFriction}
              onCopyFriction={onCopyFriction}
              onRemoveFriction={onRemoveFriction}
              onNavigateToEntity={onNavigateToEntity}
              onNavigateToRelationship={onNavigateToRelationship}
              onFetchAffectedEntities={onFetchAffectedEntities}
              onFetchAffectedRelationships={onFetchAffectedRelationships}
              onAddFriction={onAddFriction ? () => onAddFriction(framework) : undefined}
            />
          ))}
        </>
      )}
    </div>
  );
};
